<template>
  <div class="signup-wrapper">
    <div class="signup-content">
      <div class="signup-step-back">
        <v-icon color="#333333" size="22" @click="$router.go(-1)">
          mdi-arrow-left
        </v-icon>
      </div>

      <h2 class="signup-title">
        {{ $i18n.t("signup.identification.title") }}
      </h2>

      <v-form
        ref="form"
        v-model="valid"
        @submit.prevent="saveIdentificationData()"
        class="signup-form"
      >
        <div class="signup-form-label">
          {{ $i18n.t("signup.identification.contact.fields.phone.label") }}
        </div>
        <PhoneInput
          :dropdownOptions="{
            width: $vuetify.breakpoint.smAndUp ? '420px' : '330px',
            showFlags: true,
            showDialCodeInSelection: true,
            showDialCodeInList: true,
          }"
          :inputOptions="{
            placeholder: $i18n.t(
              'signup.identification.contact.fields.phone.placeholder',
            ),
          }"
          :errorMessage="
            $i18n.t('signup.identification.contact.fields.phone.invalidPhone')
          "
          @setPhoneData="setPhoneData"
        />

        <div class="signup-input-field">
          <div class="signup-form-label">
            {{ $i18n.t("signup.identification.contact.fields.address.label") }}
          </div>

          <v-autocomplete
            id="region"
            v-model="region"
            :items="regions"
            item-text="name"
            item-value="name"
            autocomplete="off"
            :placeholder="$i18n.t('signup.identification.contact.fields.address.region')"
            hide-details
            append-icon="mdi-chevron-down"
            auto-select-first
            :no-data-text="$i18n.t('base.noResults')"
            outlined
            rounded
            required
            class="signup-input-field signup-autocomplete"
            @change="handleRegionSelection"
            :menu-props="{
              rounded: 'lg',
              contentClass: 'signup-custom-menu elevation-0',
              offsetY: true,
            }"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content :id="`region-${item.code}`" class="dropdown-row">
                <v-list-item-title v-text="item.name" class="signup-dropdown-item-text" />
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </div>

        <div class="signup-input-field">
          <v-autocomplete
            id="locality"
            v-model="locality"
            :items="localitiesList"
            :placeholder="
              $i18n.t('signup.identification.contact.fields.address.locality')
            "
            hide-details
            outlined
            rounded
            required
            append-icon="mdi-chevron-down"
            auto-select-first
            :no-data-text="$i18n.t('base.noResults')"
            class="signup-input-field"
            @change="handleLocalitySelection"
            :menu-props="{
              rounded: 'lg',
              contentClass: 'signup-custom-menu elevation-0',
              offsetY: true,
            }"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content
                :id="`locality-${localitiesList.indexOf(item)}`"
                class="dropdown-row"
              >
                <v-list-item-title v-text="item" class="signup-dropdown-item-text" />
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </div>

        <v-text-field
          id="street"
          v-model="street"
          outlined
          rounded
          required
          autocomplete="off"
          :placeholder="$i18n.t('signup.identification.contact.fields.address.street')"
          :hide-details="
            street !== null && !$refs.street.isFocused ? $refs.street.validate() : ''
          "
          class="signup-input-field"
          validate-on-blur
          ref="street"
          :rules="mustCompleteRules"
        />

        <v-text-field
          id="street-number"
          v-model="streetNumber"
          outlined
          rounded
          required
          autocomplete="off"
          :placeholder="
            $i18n.t('signup.identification.contact.fields.address.streetNumber')
          "
          :hide-details="
            streetNumber !== null && !$refs.streetNumber.isFocused
              ? $refs.streetNumber.validate()
              : ''
          "
          class="signup-input-field"
          validate-on-blur
          ref="streetNumber"
          :rules="numberRules"
        />

        <div class="signup-input-field">
          <v-text-field
            v-model="interiorNumber"
            outlined
            rounded
            :placeholder="
              $i18n.t('signup.identification.contact.fields.address.interiorNumber')
            "
            required
            autocomplete="off"
            hide-details
          />
        </div>

        <NewButton
          id="next-step"
          class="signup-step-button"
          :class="{ 'signup-success-btn': signupStatus.completedIdentification }"
          :text="
            signupStatus.completedIdentification
              ? $i18n.t('signup.identification.contact.success')
              : $i18n.t('base.finish')
          "
          :type="'submit'"
          :loading="loading"
          :disabled="!valid || loading || !phoneValid"
        />

        <div
          class="signup-cancel-container"
          @click="$router.push({ name: 'signupDashboard' })"
        >
          <p class="signup-cancel">
            {{ $i18n.t("base.cancel") }}
          </p>
        </div>
      </v-form>

      <ReusableDialog
        v-model="errorDialog"
        :title="$i18n.t('signup.identification.errors.title')"
        :text="$i18n.t('signup.identification.errors.message')"
        @display="errorDialog = $event"
      >
        <template v-slot:buttonRow>
          <div class="signup-button-row">
            <div class="signup-chat-btn">
              <NewButton :text="$i18n.t('base.goToChat')" @on-click="openChat()" />
            </div>

            <div class="keep-trying-btn">
              <NewButton
                :text="$i18n.t('base.keepTrying')"
                :loading="loading"
                @on-click="errorDialog = false"
              />
            </div>
          </div>
        </template>
      </ReusableDialog>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import NewButton from "../../../components/shared/NewButton.vue";
import { addressSelects } from "../../../mixins/addressSelects.js";
import { signupFunctions } from "../../../mixins/signup.js";

import userSelects from "../../../mixins/userSelects.js";
import PhoneInput from "../../../components/shared/PhoneInput.vue";
import ReusableDialog from "../../../components/shared/ReusableDialog.vue";

export default {
  name: "ContactView",

  components: { NewButton, PhoneInput, ReusableDialog },

  mixins: [addressSelects, userSelects, signupFunctions],

  data() {
    return {
      valid: false,
      loading: false,
      place: null,
      mapOptions: {
        mapTypeControlOptions: {
          mapTypeIds: [],
        },
        fullscreenControl: false,
        streetViewControl: false,
      },
      phone: null,
      phoneValid: false,
      street: null,
      region: null,
      locality: null,
      streetNumber: null,
      country: "Chile",
      interiorNumber: null,
      phoneErrorHint: false,
      mustCompleteRules: [
        v => !!v || this.$i18n.t("signup.shared.errors.mustComplete"),
      ],
      numberRules: [
        v => !!v || this.$i18n.t("signup.shared.errors.mustComplete"),
        v => /^[-+]?\d*$/.test(v) || this.$i18n.t("signup.shared.errors.generic"),
      ],
      errorDialog: false,
    };
  },

  computed: {
    ...mapGetters(["signupIdentificationData", "signupEmail", "signupStatus"]),

    localitiesList() {
      if (!this.region) {
        return this.regions
          .map(region => region.localities)
          .flat(1)
          .sort();
      }
      return this.regions
        .find(region => region.name === this.region)
        ?.localities.sort();
    },
  },

  methods: {
    ...mapActions(["setContactData"]),

    async saveIdentificationData() {
      if (this.signupStatus.completedIdentification || this.loading) return;
      this.loading = true;
      this.setContactData({
        phone: this.phone,
        country: this.country,
        region: this.region,
        locality: this.locality,
        street: this.street,
        streetNumber: this.streetNumber,
        interiorNumber: this.interiorNumber,
      });
      const sendIdentificationDataResponse = await this.sendIdentificationData();
      this.loading = false;
      if (sendIdentificationDataResponse.error) {
        this.errorDialog = true;
      } else {
        await new Promise(resolve => setTimeout(resolve, 3000));
        this.$router.push({ name: "signupDashboard" });
      }
    },

    handleRegionSelection() {
      this.locality = null;
    },

    handleLocalitySelection(locality) {
      if (!locality) return;
      this.region = this.regions.find(region => region?.localities.includes(locality))?.name;
    },

    setPhoneData(data) {
      this.phoneValid = data.valid;
      this.phone = data.number;
    },

    reloadData() {
      if (this.signupIdentificationData.street) {
        this.street = this.signupIdentificationData.street;
      }
      if (this.signupIdentificationData.streetNumber) {
        this.streetNumber = this.signupIdentificationData.streetNumber;
      }
      if (this.signupIdentificationData.region) {
        this.region = this.signupIdentificationData.region;
      }
      if (this.signupIdentificationData.locality) {
        this.locality = this.signupIdentificationData.locality;
      }
      if (this.signupIdentificationData.interiorNumber) {
        this.interiorNumber = this.signupIdentificationData.interiorNumber;
      }
    },

    openChat() {
      this.$intercom.showNewMessage(
        this.$i18n.t("signup.identification.errors.intercom.failedResponse"),
      );
    },
  },

  mounted() {
    this.reloadData();
  },
};
</script>

<style lang="scss" scoped>
.spouse-fields > :not(:last-child) {
  margin-bottom: 10px;
}

.signup-chat-btn::v-deep .new-btn {
  background-color: #ffffff !important;
  border: 1px solid #000000 !important;
  span {
    color: #000000 !important;
  }
}

.signup-success-btn {
  background-color: #6d48ff !important;
  border: unset;
}
</style>
